/**
 * 需求 R133（PM 2026-08-21 拍板 Q3=A「要加保護（例如不自動重算或提示）」）
 *
 * ── 這支模組在防什麼
 * 本票的根因是後端 quoteItemSchema 沒宣告 customCostEnabled / customCostValue，mongoose
 * strict mode 把兩個值靜默丟棄（已於 src/server/models/Quote.js 修好）。但修復**之前**存下來的
 * 舊單有一個殘留風險：
 *   ‧ 存檔當下的 item.unitPrice 是「已套用自定成本」算出來的，而且 item.baseCost 存的正是
 *     getEffectiveBaseCost() 的結果 ＝ 那個自定成本數字（QuoteEditPage.updateCustomCost 會寫
 *     item.baseCost = getEffectiveBaseCost(item)，存檔 payload 再原樣送出）。
 *   ‧ 但 customCostEnabled 讀回是 false（schema default），所以只要有人在編輯頁切換成本來源，
 *     getEffectiveBaseCost 就會退回來源成本 → 單價被**無聲**改回非自定成本的價格。
 * PM Q3=A 要求為這種舊單加保護，這裡提供判定；「怎麼保護」由 QuoteEditPage 實作
 * （不自動重算 unitPrice、擋掉會改寫 baseCost 的操作、抽屜顯示提示與解除保護鈕）。
 *
 * ── 判定訊號為什麼是 baseCost ≠ 來源成本
 * 被丟掉的是「旗標與數值」，但數值其實還躺在 item.baseCost 裡（它是計算基準的快照）。
 * 因此「存檔的 baseCost 無法用目前的來源成本重現」＝ 存檔單價的成本基準不是來源成本
 * ＝ 極可能就是被丟棄的自定成本。這比拿存檔單價回推可靠得多（單價還混了階數／材積／
 * 全單加成／單品加價／貼標費／匯率，任何一項變動都會誤判）。
 *
 * ── 已知的誤判方向（刻意選擇「寧可多提示」）
 * 若產品主檔的來源成本在報價單存檔之後被調整過，stored baseCost 同樣會對不上 → 也會進保護。
 * 這種列的風險其實是同一種（一按重算單價就變），提示無害且可一鍵解除，故不另外排除。
 * 反向的誤判（該保護卻沒保護）才是真正有害的，所以拿不到來源成本時**不**進保護：
 * 那代表我們根本沒有比較基準，硬猜只會讓正常的單被凍住。
 *
 * ── 為什麼還要一個時間界線
 * 修復上線之後存的單，兩個欄位一定有明確值（前端 payload 一律送出），不需要也不該再被保護；
 * 只有 updatedAt 早於界線的單才有「值被丟掉」的可能。沒有時間戳（極舊資料）時一律當舊單，
 * 保護優先。
 */

// 本次修復（quoteItemSchema 補上兩個欄位）上線的時間界線。
// 取台北時間 2026-08-21 00:00 ＝ 本票修復當日零時：這一天之後存的單才可能帶著正確的
// customCostEnabled / customCostValue。若實際部署晚於這個時刻，落在「界線 ~ 部署」之間存的單
// 不會進保護，但那些單的 baseCost 與來源成本本來就一致（沒有自定成本可丟），影響為零。
export const CUSTOM_COST_PERSISTED_SINCE = '2026-08-21T00:00:00+08:00';

/** 這張報價單是否存於「修復上線」之前（＝自定成本可能已被 strict mode 丟棄）。 */
export function isQuoteSavedBeforeCustomCostFix(quoteUpdatedAt, since = CUSTOM_COST_PERSISTED_SINCE) {
  if (!quoteUpdatedAt) return true; // 沒有時間戳＝無從判斷，保護優先
  const t = new Date(quoteUpdatedAt).getTime();
  if (!Number.isFinite(t)) return true;
  return t < new Date(since).getTime();
}

/**
 * 這一列是否為「修復前遺失的自定成本」殘留 → 需要保護。
 * @param {object}  args.item            報價單品項（DB 讀回後的值）
 * @param {number}  args.sourceCost      目前成本來源算出來的標準成本（拿不到請傳 null）
 * @param {boolean} args.savedBeforeFix  isQuoteSavedBeforeCustomCostFix() 的結果
 */
export function isLegacyCustomCostItem({ item, sourceCost, savedBeforeFix }) {
  if (!savedBeforeFix || !item) return false;
  // 旗標為真＝修復後存的、資料完整，正常走既有邏輯。
  // 需求 R149：新增的「自訂箱成本」旗標同樣要放行。它是 2026-08-29 才上線的欄位，
  // 一定晚於本檔的時間界線（2026-08-21），旗標為真代表這列的 baseCost 是**新規則下正常算出來的
  // 包成本**（自訂箱成本 ÷ 箱入數），與來源成本不同是理所當然。不放行的話，這種列會被誤判成
  // 「修復前遺失的自定成本」而被凍結重算 —— 使用者一改箱成本，單價卻不動（AC18）。
  if (item.customCostEnabled || item.customBoxCostEnabled) return false;
  // 手動單價的列本來就不會被自動重算（calcUnitPrice 開頭早退），沒有這個風險。
  if (item.isManualPrice) return false;
  const stored = Number(item.baseCost);
  const source = Number(sourceCost);
  if (!Number.isFinite(stored) || stored <= 0) return false;
  if (sourceCost == null || !Number.isFinite(source)) return false;
  if (Math.abs(stored - source) <= 1e-6) return false;
  // 需求 R132：報價單的成本基準已由「DB 的已進位 unitPrice」改成「箱價÷箱入數的未進位原值」
  // （PM 2026-08-27 案例：59.9 → 59.85）。本票之前存下來的單，baseCost 存的是舊規則下
  // 完全正常的已進位值，若不放行，全部舊單都會被誤判成「遺失的自定成本」而被凍結＋跳錯誤提示。
  // 故再比一次「來源成本套用舊規則（無條件進位到小數 1 位）」的值；對得上就是舊規則的正常列。
  // 注意這只放寬「stored 恰為 source 的舊規則進位值」這一種，真正的自定成本（人手填的數字）
  // 幾乎不可能剛好等於 ceil(來源成本 ×10)/10，保護強度不受影響。
  const legacyRounded = Math.ceil(Number((source * 10).toFixed(6))) / 10;
  return Math.abs(stored - legacyRounded) > 1e-6;
}
